"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const processes = [{
  batch: "PROC_001",
  status: "ACTIVE",
  name: "Precision Die-Cutting",
  descriptor: "High-speed rotary and flatbed systems",
  abv: "0.001",
  ibu: "99",
  srm: "0",
  note: "Tight-tolerance conversion for complex industrial geometries.",
  category: "DIE-CUTTING",
  image: "https://gstna.com/wp-content/uploads/2026/07/die-cut-5-scaled.png?v=dlu5ptzcsek",
  srcset: "https://gstna.com/wp-content/uploads/2026/07/die-cut.png?v=uakdy0fjrt-wvc-srcset",
  sizes: "https://gstna.com/wp-content/uploads/2026/07/die-cut.png?v=4670h2rrqtn-wvc-sizes"
}, {
  batch: "PROC_002",
  status: "ACTIVE",
  name: "Lathe Slitting",
  descriptor: "Precision-engineered roll conversion",
  abv: "0.002",
  ibu: "95",
  srm: "0",
  note: "Clean-edge slitting for films, tapes, and industrial adhesives.",
  category: "SLITTING",
  image: "https://gstna.com/wp-content/uploads/2026/07/jumbo-roll-slitting-2-scaled.png?v=ryxo6x750gd",
  srcset: "https://gstna.com/wp-content/uploads/2026/07/slitter.png?v=dap3isn5m87-wvc-srcset",
  sizes: "https://gstna.com/wp-content/uploads/2026/07/slitter.png?v=6b3am7rx71a-wvc-sizes"
}, {
  batch: "PROC_003",
  status: "ACTIVE",
  name: "Multi-Layer Laminating",
  descriptor: "Advanced adhesive bonding technology",
  abv: "0.003",
  ibu: "98",
  srm: "0",
  note: "Precision bonding of multi-layer films and substrates.",
  category: "LAMINATING",
  image: "https://gstna.com/wp-content/uploads/2026/07/laminating-2-scaled.png?v=biex9frb0cj",
  srcset: "https://gstna.com/wp-content/uploads/2026/07/laminating-2-scaled.png?v=ulntzi1c21-wvc-srcset",
  sizes: "https://gstna.com/wp-content/uploads/2026/07/laminating-2-scaled.png?v=2bqd33use1j-wvc-sizes"
}];
const filters = ["ALL", "DIE-CUTTING", "SLITTING", "LAMINATING"];
export default function Processes() {
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [sort, setSort] = useState("latest");
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const filtered = processes.filter(b => {
    if (activeFilter === "ALL") return true;
    return b.category === activeFilter;
  });
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "latest") return 0;
    return 0;
  });
  const statusStyle = (status: string) => {
    if (status === "ACTIVE") return "bg-blue-600 text-white border border-blue-600";
    return "bg-slate-200 text-slate-700 border border-slate-300";
  };
  return <section data-section-id="2665" id="core-engineering-processes" className="dark bg-slate-50 text-slate-900 pt-32 md:pt-[200px] pb-24 md:pb-[160px] px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div ref={ref} className={`mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="font-mono text-xs md:text-sm uppercase tracking-widest mb-8 text-blue-600">
            GST_REF: ENGINEERING // CAPABILITIES
          </div>
          <h1 className="font-default font-bold uppercase leading-[1.1] text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-10 tracking-tighter">
            PRECISION
            <br />
            <span className="text-blue-600">CONVERTING</span>
            <br />
            SOLUTIONS
          </h1>
          <p className="font-default text-xl md:text-2xl max-w-[720px] leading-relaxed text-slate-700">
            Delivering customized die-cutting, slitting, and laminating services for industrial manufacturers across North America.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-12 md:mb-16 pb-8 border-b border-slate-200">
          <ToggleGroup type="single" value={activeFilter} onValueChange={v => v && setActiveFilter(v)} className="flex flex-wrap gap-2 lg:gap-3 bg-white border border-slate-200 p-2">
            {filters.map((f, i) => <ToggleGroupItem key={f} data-index={i} value={f} className="inline-flex items-center justify-center gap-2 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none aria-invalid:ring-destructive/20 aria-invalid:border-destructive whitespace-nowrap bg-transparent h-9 min-w-0 flex-1 shrink-0 rounded-none shadow-none focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l font-default text-xs uppercase tracking-widest px-4 py-2 border border-transparent data-[state=on]:bg-blue-600 data-[state=on]:text-white transition-colors !text-[rgba(1,0,2,1)] hover:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 first:rounded-l-md last:rounded-r-md focus:z-10 hover:bg-slate-100">
                {f}
              </ToggleGroupItem>)}
          </ToggleGroup>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex w-fit items-center justify-between gap-2 rounded-md whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 font-default text-xs uppercase tracking-widest bg-white border border-slate-200 px-5 py-6 min-w-[180px] !text-[rgba(0,0,0,1)] !font-bold dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:hover:bg-input/50 disabled:cursor-not-allowed disabled:opacity-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="latest" className="font-default text-xs uppercase tracking-widest">
                LATEST
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sorted.map((process, i) => <ProcessCard key={process.batch} process={process} index={i} statusStyle={statusStyle} />)}
        </div>

        {sorted.length === 0 && <div className="text-center py-24 font-default text-sm uppercase tracking-widest text-slate-500">
            NO PROCESSES MATCH THIS FILTER
          </div>}
      </div>
    </section>;
}
function ProcessCard({
  process,
  index,
  statusStyle
}: {
  process: {
    batch: string;
    status: string;
    name: string;
    descriptor: string;
    abv: string;
    ibu: string;
    srm: string;
    note: string;
    image: string;
    srcset: string;
    sizes: string;
  };
  index: number;
  statusStyle: (s: string) => string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`group bg-white border border-slate-200 shadow-md p-8 md:p-10 flex flex-col aspect-[4/5] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index % 6 * 80}ms`
  }}>
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
          {process.batch}
        </span>
        <span className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 ${statusStyle(process.status)}`}>
          {process.status}
        </span>
      </div>

      <div className="relative w-full aspect-square mb-6 overflow-hidden bg-slate-100">
        <img src={process.image} data-wvc-srcset={process.srcset} data-wvc-sizes={process.sizes} alt={process.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
      </div>

      <h3 className="font-default font-bold uppercase text-3xl leading-none mb-2 tracking-tighter !text-[rgba(21,0,21,1)] md:text-[2.25rem]">
        {process.name}
      </h3>

      <p className="font-default text-base mb-5 text-slate-600">
        {process.descriptor}
      </p>

      <div className="h-px w-full mb-5 bg-blue-600" />

      <div className="mt-auto pt-4 border-t border-slate-100 flex flex-col gap-4">
        <p className="font-default text-sm leading-snug line-clamp-2 text-slate-700">
          {process.note}
        </p>
        <Button variant="link" className="font-default text-xs uppercase tracking-widest p-0 h-auto justify-start hover:no-underline text-blue-600">
          VIEW SPECIFICATIONS
          <ArrowRight className="ml-1 size-3" />
        </Button>
      </div>
    </div>;
}